import type { OrgModelRecord, OrgModelType } from "@/app/providers/services";
import { ContactSearchField } from "@/features/contacts";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import DateTimeField from "@imminently/decisively-ui/DateTimeField";
import Dialog from "@imminently/decisively-ui/Dialog";
import Form from "@imminently/decisively-ui/Form";
import SelectField from "@imminently/decisively-ui/SelectField";
import TextField from "@imminently/decisively-ui/TextField";
import { useEffect, useMemo, useRef } from "react";
// decisively-ui's useWatch/useFormContext wrapper loses per-field type narrowing
// (see gotchas.md); react-hook-form's own hooks work directly against the same
// FormProvider context that decisively-ui's <Form> mounts, with correct typing.
import { useFormContext, useWatch } from "react-hook-form";
import { z } from "zod";
import { SOURCE_OPTIONS, TYPE_OPTIONS, type FormMode, type FormState } from "../types";
import {
  CREATE_TYPE_OPTIONS,
  buildOrgModelRelationSummary,
  getInitialFormState,
  getOrgModelFormRelatedIdLabel,
  getOrgModelFormSubjectIdLabel,
} from "../utils";

const ORG_MODEL_TYPE_VALUES = [
  "parent",
  "approver",
  "member",
  "delegate",
  "resolvedApprover",
  "hierarchySummary",
  "hierarchyIssue",
] as const satisfies readonly OrgModelType[];

const orgModelFormSchema = z
  .object({
    externalId: z.string(),
    type: z.enum(ORG_MODEL_TYPE_VALUES),
    subjectType: z.string().trim().min(1, "Required"),
    subjectId: z.string().trim().min(1, "Required"),
    relatedId: z.string().trim().min(1, "Required"),
    validFrom: z.date().nullable(),
    validTo: z.date().nullable(),
    level: z.string(),
    source: z.enum(["", ...SOURCE_OPTIONS]),
  })
  .superRefine((data, ctx) => {
    if (data.type === "parent" && data.subjectId.trim() === data.relatedId.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["relatedId"],
        message: "Parent records cannot use the same subjectId and relatedId.",
      });
    }
    if (data.type === "delegate" && data.subjectType.trim() !== "contact") {
      ctx.addIssue({
        code: "custom",
        path: ["subjectType"],
        message: "Delegate records must use subjectType='contact'.",
      });
    }
    if (data.validFrom && data.validTo && data.validFrom >= data.validTo) {
      ctx.addIssue({
        code: "custom",
        path: ["validTo"],
        message: "Valid From must be earlier than Valid To.",
      });
    }
  });

const SOURCE_SELECT_OPTIONS = [
  { value: "", label: "(none)" },
  ...SOURCE_OPTIONS.map((source) => ({ value: source, label: source })),
];

type OrgModelFormDialogProps = {
  open: boolean;
  mode: FormMode;
  record?: OrgModelRecord | null;
  loading?: boolean;
  onClose: () => void;
  onSave: (state: FormState) => Promise<void>;
};

type OrgModelFormFieldsProps = {
  mode: FormMode;
  isCreate: boolean;
  isReadOnly: boolean;
};

/** Rendered inside <Form> so it can watch live field values via RHF context. */
const OrgModelFormFields = ({ mode, isCreate, isReadOnly }: OrgModelFormFieldsProps) => {
  const { control, setValue } = useFormContext<FormState>();
  const type = useWatch<FormState, "type">({ control, name: "type" });
  const subjectType = useWatch<FormState, "subjectType">({ control, name: "subjectType" });
  const subjectId = useWatch<FormState, "subjectId">({ control, name: "subjectId" });
  const relatedId = useWatch<FormState, "relatedId">({ control, name: "relatedId" });

  const typeOptions = isCreate ? CREATE_TYPE_OPTIONS : TYPE_OPTIONS;
  const typeSelectOptions = useMemo(() => typeOptions.map((t) => ({ value: t, label: t })), [typeOptions]);
  const isDelegate = type === "delegate";
  const usesRelatedContact = type === "approver" || type === "member" || type === "delegate";
  const usesSubjectContact = type === "delegate";
  const subjectIdLabel = getOrgModelFormSubjectIdLabel(type);
  const relatedIdLabel = getOrgModelFormRelatedIdLabel(type);
  const relationSummary = useMemo(
    () => buildOrgModelRelationSummary(type, subjectType, subjectId, relatedId),
    [type, subjectType, subjectId, relatedId],
  );
  const relatedIdHint = [
    type === "parent" ? "Parent links use `relatedId` as the parent entity ID." : null,
    "Stored in the org model record as `relatedId`.",
  ]
    .filter(Boolean)
    .join(" ");

  // Mirrors the old onTypeChange side effect: auto-fill subjectType when the user
  // switches to "delegate", but don't touch it on initial mount (existing records).
  const skipNextTypeEffect = useRef(true);
  useEffect(() => {
    if (skipNextTypeEffect.current) {
      skipNextTypeEffect.current = false;
      return;
    }
    if (type === "delegate") {
      setValue("subjectType", "contact", { shouldDirty: true, shouldValidate: true });
    }
  }, [type, setValue]);

  return (
    <>
      {isCreate ? (
        <TextField
          name="externalId"
          label="External ID (optional)"
          fullWidth
          disabled={isReadOnly}
        />
      ) : null}

      <SelectField
        name="type"
        label="Type"
        options={typeSelectOptions}
        disabled={isReadOnly}
        clearable={false}
      />

      <TextField
        name="subjectType"
        label="Subject Type"
        required
        fullWidth
        disabled={isDelegate || isReadOnly}
        helperText={isDelegate ? "Delegate records require `subjectType` = `contact`." : undefined}
      />

      {usesSubjectContact ? (
        <ContactSearchField
          name="subjectId"
          label={subjectIdLabel}
          required
          disabled={isReadOnly}
        />
      ) : (
        <TextField
          name="subjectId"
          label={subjectIdLabel}
          required
          fullWidth
          disabled={isReadOnly}
        />
      )}
      {usesRelatedContact ? (
        <ContactSearchField
          name="relatedId"
          label={relatedIdLabel}
          required
          disabled={isReadOnly}
          helperText={relatedIdHint}
        />
      ) : (
        <TextField
          name="relatedId"
          label={relatedIdLabel}
          required
          fullWidth
          disabled={isReadOnly}
          helperText={relatedIdHint}
        />
      )}

      {relationSummary ? (
        <div className="rounded border border-border bg-secondary px-3 py-2.5">
          <p className="text-sm">{relationSummary}</p>
        </div>
      ) : null}

      <DateTimeField<Date | null>
        name="validFrom"
        label="Valid From (optional)"
        serialize={false}
        fullWidth
        disabled={isReadOnly}
      />
      <DateTimeField<Date | null>
        name="validTo"
        label="Valid To (optional)"
        serialize={false}
        fullWidth
        disabled={isReadOnly}
      />

      {mode !== "create" ? (
        <>
          <TextField
            name="level"
            label="Level (optional)"
            type="number"
            fullWidth
            disabled={isReadOnly}
          />
          <SelectField
            name="source"
            label="Source (optional)"
            options={SOURCE_SELECT_OPTIONS}
            disabled={isReadOnly}
            clearable={false}
          />
        </>
      ) : null}
    </>
  );
};

export const OrgModelFormDialog = (props: OrgModelFormDialogProps) => {
  const { open, mode, record, loading, onClose, onSave } = props;
  const isCreate = mode === "create";
  const isReadOnly = mode === "view";

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => !nextOpen && onClose()}
      intent="form"
    >
      <Dialog.Content className="flex flex-col">
        <DialogHeader
          title={isReadOnly ? "View Org Model Element" : isCreate ? "Create Org Model Element" : "Edit Org Model Element"}
        />
        <Form<FormState, typeof orgModelFormSchema>
          schema={orgModelFormSchema}
          defaultValues={getInitialFormState(mode, record)}
          onSubmit={async (values) => {
            if (isReadOnly) return;
            await onSave(values);
          }}
          className="flex flex-col"
        >
          <Dialog.Body className="grid grid-cols-1 gap-3">
            <OrgModelFormFields
              mode={mode}
              isCreate={isCreate}
              isReadOnly={isReadOnly}
            />
          </Dialog.Body>
          <Dialog.Actions>
            <Dialog.Actions.CancelButton
              label={isReadOnly ? "Close" : "Cancel"}
              disabled={loading}
            >
              {isReadOnly ? "Close" : "Cancel"}
            </Dialog.Actions.CancelButton>
            {!isReadOnly ? (
              <Dialog.Actions.PrimaryButton
                label={isCreate ? "Create" : "Save"}
                type="submit"
                loading={loading}
                disabled={loading}
              >
                {isCreate ? "Create" : "Save"}
              </Dialog.Actions.PrimaryButton>
            ) : null}
          </Dialog.Actions>
        </Form>
      </Dialog.Content>
    </Dialog>
  );
};

export default OrgModelFormDialog;
